import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-bg text-white">
      <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
        <div className="mb-8">
          <Link 
            to="/" 
            className="text-blue-400 hover:text-blue-300 transition-colors inline-flex items-center gap-2 mb-6"
          >
            ← Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-white/60 text-sm">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8 text-white/80">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
            <p>
              GenAnything ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service, including our website, mobile application, and related services (collectively, the "Service").
            </p>
            <p>
              By using our Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, do not use our Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account Information:</strong> Email address, username, password, and profile information when you create an account</li>
              <li><strong>Payment Information:</strong> Payment method details processed securely through Stripe. We do not store your full credit card information on our servers</li>
              <li><strong>Content:</strong> Images, videos, prompts, and other content you upload or generate through our Service</li>
              <li><strong>Communication:</strong> Information you provide when contacting us for support or feedback</li>
            </ul>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">2.2 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Usage Data:</strong> Information about how you access and use our Service, including IP address, browser type, device information, pages visited, and time spent on pages</li>
              <li><strong>Cookies and Tracking Technologies:</strong> We use cookies, web beacons, and similar technologies to track activity and hold certain information</li>
              <li><strong>Log Data:</strong> Server logs including timestamps, request details, and error information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide, maintain, and improve our Service</li>
              <li>To process payments and manage subscriptions through Stripe</li>
              <li>To generate videos and other content based on your inputs</li>
              <li>To communicate with you about your account, transactions, and service updates</li>
              <li>To send you marketing communications (with your consent, which you can opt out of at any time)</li>
              <li>To detect, prevent, and address technical issues and security threats</li>
              <li>To comply with legal obligations and enforce our Terms of Service</li>
              <li>To analyze usage patterns and improve user experience</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. Payment Processing and Stripe</h2>
            <p>
              We use Stripe, Inc. ("Stripe") to process payments. When you make a purchase or subscribe to our Service, your payment information is collected and processed by Stripe in accordance with their Privacy Policy.
            </p>
            <p>
              <strong>What Stripe Collects:</strong> Stripe collects payment method information (credit card numbers, billing addresses, etc.) and transaction data. We do not have access to your full credit card details.
            </p>
            <p>
              <strong>How Stripe Uses Your Data:</strong> Stripe uses your payment information to process transactions, prevent fraud, and comply with financial regulations. For more information, please review Stripe's Privacy Policy at{' '}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
                https://stripe.com/privacy
              </a>.
            </p>
            <p>
              <strong>What We Receive:</strong> We receive transaction confirmations, subscription status, and billing information necessary to provide our Service and manage your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Third-Party Services</h2>
            <p>We use the following third-party services that may collect information:</p>
            
            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">5.1 Stripe</h3>
            <p>Payment processing service. See Section 4 above for details.</p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">5.2 Supabase</h3>
            <p>
              We use Supabase for authentication, database storage, and user management. Your account information and content are stored on Supabase's servers. Review their Privacy Policy at{' '}
              <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
                https://supabase.com/privacy
              </a>.
            </p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">5.3 Amazon Web Services (AWS)</h3>
            <p>
              We use AWS S3 for storing your uploaded images and generated videos. Your content is stored on AWS servers. Review AWS's Privacy Policy at{' '}
              <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
                https://aws.amazon.com/privacy/
              </a>.
            </p>

            <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">5.4 Replicate</h3>
            <p>
              We use Replicate for AI video generation. Your images and prompts may be processed by Replicate's services. Review their Privacy Policy at{' '}
              <a href="https://replicate.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline">
                https://replicate.com/privacy
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption of data in transit using SSL/TLS</li>
              <li>Secure authentication and authorization systems</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and employee training</li>
              <li>Secure payment processing through Stripe</li>
            </ul>
            <p className="mt-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Account Deletion:</strong> Delete your account and associated data</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at the email address provided in Section 11. We will respond to your request within a reasonable timeframe.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to track activity on our Service and hold certain information. Cookies are files with a small amount of data that may include an anonymous unique identifier.
            </p>
            <p>Types of cookies we use:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for the Service to function properly</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how users interact with our Service</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            </ul>
            <p className="mt-4">
              You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to provide our Service, comply with legal obligations, resolve disputes, and enforce our agreements. When you delete your account, we will delete or anonymize your personal information, except where we are required to retain it for legal or legitimate business purposes.
            </p>
            <p className="mt-4">
              Content you upload or generate may be retained for a reasonable period after account deletion to ensure service continuity and comply with legal requirements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. Children's Privacy</h2>
            <p>
              Our Service is not intended for children under the age of 13 (or the applicable age of majority in your jurisdiction). We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using our Service, you consent to the transfer of your information to these countries.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to This Privacy Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
            <p className="mt-4">
              Material changes will be communicated via email or a prominent notice on our Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-4 p-4 bg-white/5 rounded-lg">
              <p><strong>Email:</strong> privacy@genanything.ai</p>
              <p className="mt-2"><strong>Website:</strong> <a href="https://genanything.ai" className="text-blue-400 hover:text-blue-300 underline">https://genanything.ai</a></p>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-white/60 text-sm text-center">
            By using GenAnything, you acknowledge that you have read and understood this Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
}


